import { Component, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { Endpoint, HelpIndex, MessageType, UpdateStatusType } from '../../../models/enums';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { NameDescriptionComponent } from '../../name-description/name-description.component';
import { TargetPickerTreeComponent, TargetPickerTreeType } from '../../target-picker-tree/target-picker-tree.component';
import { DrObject, DrObjectType } from '../../../models/dr-object';
import * as _ from 'underscore';
import { View, ViewType } from '../../../models/view';

@Component({
    standalone: false,
    selector: 'res-create-edit-comparison-modal',
    templateUrl: './create-edit-comparison-modal.component.html',
    styleUrls: ['./create-edit-comparison-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class CreateEditComparisonModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(NameDescriptionComponent, { static: true }) nameDescriptionComponent: NameDescriptionComponent;
    @ViewChildren(TargetPickerTreeComponent) targetPickerTreeComponents: QueryList<TargetPickerTreeComponent>;

    modalTitle = 'New Comparison';
    primaryButtonLabel = 'OK';
    secondaryButtonLabel = 'Cancel';
    maximizable: false;
    modalHelpIndex: HelpIndex.myedisc_create_comparison_dlg;
    modalAutomationId = 'comparison';
    modalClass = 'comparison-modal';
    isBusy = false;
    isBusyMessage = '';
    targetPickerTreeType: TargetPickerTreeType;
    dataSetFilterVisible: boolean;
    dataSetFilterQuery = '';
    isEdit = false;
    viewA: string;
    viewB: string;
    preselectNodePredicateA: (value: GenericTreeNode, index: number, obj: GenericTreeNode[]) => boolean;
    preselectNodePredicateB: (value: GenericTreeNode, index: number, obj: GenericTreeNode[]) => boolean;
    getSelectedSearchNodeCallbackA: (searchResultsList: any[]) => any;
    getSelectedSearchNodeCallbackB: (searchResultsList: any[]) => any;

    // TODO: need TargetPicker to set the isDirty flag properly.

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService) {
        super(modalService, modalRef);
        this.setHelpIndex();
    }

    ngOnInit(): void {
        this.nameDescriptionComponent.drObject = new DrObject(DrObjectType.COMPARISONS);
        if (this.modalData) {
            if (this.modalData.name) {
                this.modalTitle = 'Edit Comparison';
                this.nameDescriptionComponent.name = this.modalData.name;
                this.nameDescriptionComponent.description = this.modalData.description;
                this.isEdit = true;
                this.viewA = this.modalData.viewA.handle;
                this.viewB = this.modalData.viewB.handle;
                this.preselectNodePredicateA = (value: GenericTreeNode, index: number, obj: GenericTreeNode[]) => {
                    if (value.data) {
                        if (value.data.view) {
                            return value.data.view.handle === this.viewA;
                        }
                        return value.data.handle === this.viewA;
                    }
                };
                this.preselectNodePredicateB = (value: GenericTreeNode, index: number, obj: GenericTreeNode[]) => {
                    if (value.data) {
                        if (value.data.view) {
                            return value.data.view.handle === this.viewB;
                        }
                        return value.data.handle === this.viewB;
                    }
                };
                this.getSelectedSearchNodeCallbackA = (currentSearchResultList: any[]) => {
                    return this.findSearchNodeInList(this.modalData.viewA, currentSearchResultList);
                };
                this.getSelectedSearchNodeCallbackB = (currentSearchResultList: any[]) => {
                    return this.findSearchNodeInList(this.modalData.viewB, currentSearchResultList);
                };
            } else {
                this.modalTitle = 'New Comparison';
            }
        }

        this.nameDescriptionComponent.validateNameRequest = {
            endpoint: Endpoint.viewManager_validateName,
            request: (name) => {
                return {
                    name: this.nameDescriptionComponent.name,
                    projectHandle: this.commonService.currentProject.handle,
                    objectType: this.nameDescriptionComponent.drObject.objectType
                };
            }
        };

        this.targetPickerTreeType = TargetPickerTreeType.createComparison;
    }

    findSearchNodeInList(selectedComparisonView: any, currentSearchResultList: any[]): any {
        if (currentSearchResultList && selectedComparisonView && (selectedComparisonView.viewType === ViewType.CORPUS_RESULT ||
            selectedComparisonView.viewType === ViewType.CORPUS_SET_RESULT || selectedComparisonView.viewType === ViewType.WORKING_SET_RESULT)) {

            const searchEntryIndex = currentSearchResultList.findIndex(s => s && s.handle === selectedComparisonView.handle);
            if (searchEntryIndex === -1) {
                return selectedComparisonView;
            }
        }
        return null;
    }

    isTargetPickerComplete(): boolean {
        let targetPickerComplete = false;
        let firstNodeSelected = false;
        if (this.targetPickerTreeComponents) {
            if (this.targetPickerTreeComponents.first && this.targetPickerTreeComponents.first.selectedNode && this.targetPickerTreeComponents.first.selectedNode) {
                if (_.isEmpty(this.targetPickerTreeComponents.first.selectedNode.data.handle) === false) {
                    firstNodeSelected = true;
                }
            }

            if (firstNodeSelected && this.targetPickerTreeComponents.last && this.targetPickerTreeComponents.last.selectedNode && this.targetPickerTreeComponents.first.selectedNode) {
                if (_.isEmpty(this.targetPickerTreeComponents.last.selectedNode.data.handle) === false) {
                    targetPickerComplete = true;
                }
            }
        }
        return targetPickerComplete;
    }

    isTargetPickerDirty(): boolean {
        let targetPickerDirty = false;
        if (this.targetPickerTreeComponents) {
            if (this.targetPickerTreeComponents.first && this.targetPickerTreeComponents.first.selectedNode && this.modalData.viewA) {
                targetPickerDirty = !this.sameItem(this.targetPickerTreeComponents.first.selectedNode, this.modalData.viewA);
            }

            if ((targetPickerDirty === false) && this.targetPickerTreeComponents.last && this.targetPickerTreeComponents.last.selectedNode && this.modalData.viewB) {
                targetPickerDirty = !this.sameItem(this.targetPickerTreeComponents.last.selectedNode, this.modalData.viewB);
            }
        }
        return targetPickerDirty;
    }

    sameItem(selectedItem: GenericTreeNode, view: View): boolean {
        let same = false;
        if (selectedItem && selectedItem.data && view) {
            if (selectedItem.data.view) {
                same = selectedItem.data.view.handle === view.handle;
            } else {
                same = selectedItem.data.handle === view.handle;
            }
        }
        return same;
    }

    setHelpIndex(): void {
        this.modalHelpIndex = HelpIndex.myedisc_create_comparison_dlg;
    }

    isPrimaryButtonEnabled(): boolean {
        let enabled = false;
        if (!DrObject.isEmpty(this.nameDescriptionComponent.name)) {
            if (this.nameDescriptionComponent.isNameDirty() && (this.nameDescriptionComponent.formValid) && this.isTargetPickerComplete()) {
                enabled = true;
            }

            if ((enabled === false) && (this.nameDescriptionComponent.isDescriptionDirty()) && (this.nameDescriptionComponent.formValid) && this.isTargetPickerComplete()) {
                enabled = true;
            }

            if (!this.nameDescriptionComponent.isDescriptionDirty() && !this.nameDescriptionComponent.isNameDirty() && this.isTargetPickerDirty()) {
                enabled = true;
            }
        }
        return enabled;
    }

    ok(): void {
        if (this.nameDescriptionComponent.isNameDirty()) {
            this.nameDescriptionComponent.validate().then(valid => {
                if (valid) {
                    this.saveChanges();
                }
            });
        } else {
            if (this.nameDescriptionComponent.isDescriptionDirty() || this.isTargetPickerDirty()) {
                this.saveChanges();
            }
        }
    }

    saveChanges(): void {
        this.isBusy = true;
        if (this.targetPickerTreeComponents) {
            if (this.targetPickerTreeComponents.first.selectedNode) {
                if (this.targetPickerTreeComponents.first.selectedNode.data.view) {
                    this.viewA = this.targetPickerTreeComponents.first.selectedNode.data.view.handle;
                } else {
                    this.viewA = this.targetPickerTreeComponents.first.selectedNode.data.handle;
                }
                if (this.targetPickerTreeComponents.last.selectedNode.data.view) {
                    this.viewB = this.targetPickerTreeComponents.last.selectedNode.data.view.handle;
                } else {
                    this.viewB = this.targetPickerTreeComponents.last.selectedNode.data.handle;
                }
            }
        }
        const params = {
            name: this.nameDescriptionComponent.name,
            description: this.nameDescriptionComponent.description,
            viewHandleA: this.viewA,
            viewHandleB: this.viewB
        };
        let apiToCall = null;
        if (this.isEdit) {
            this.isBusyMessage = 'Updating comparison ...';
            Object.assign(params, { handle: this.modalData.handle });
            apiToCall = Endpoint.comparisonManager_updateComparison;
        } else {
            this.isBusyMessage = 'Creating comparison ...';
            Object.assign(params, { projectHandle: this.commonService.currentProject.handle });
            apiToCall = Endpoint.projectManager_createComparison;
        }
        this.restService.post(apiToCall, params).then(response => {
            if (response) {
                this.isBusy = false;
                this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.COMPARISON);
                this.closeModal(true, { name: this.nameDescriptionComponent.name, entityType: UpdateStatusType.COMPARISON });
            }
        }, () => {
            this.isBusy = false;
            this.closeModal(false);
        });
    }
}
